import { useCallback, useEffect, useState } from 'react';
import { api, type SuppressorRow } from '../lib/api.js';
import { SuppressorEditor } from '../components/SuppressorEditor.js';

const PAGE_SIZE = 100;

export function NeedsReviewPage(): JSX.Element {
  const [items, setItems] = useState<SuppressorRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SuppressorRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.suppressors.list({ needs_review: '1', limit: PAGE_SIZE, offset });
      if (offset > 0 && offset >= r.total) {
        setOffset(r.total === 0 ? 0 : Math.floor((r.total - 1) / PAGE_SIZE) * PAGE_SIZE);
        return;
      }
      setItems(r.items);
      setTotal(r.total);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <>
      <h1>Needs Review</h1>
      <p style={{ color: 'var(--muted)' }}>
        Rows the spreadsheet importer flagged for human attention, including missing dimensions,
        incomplete fitment pairs, and unverified audit statuses. Resolve each one, then uncheck
        "needs review" to send it to the live catalog.
      </p>
      {error && <div className="notice error">{error}</div>}
      {!loading && items.length === 0 && (
        <div className="notice ok">No rows need review. Catalog is clean.</div>
      )}

      <div style={{ marginBottom: 12, color: 'var(--muted)', fontSize: 12 }}>
        {loading ? 'Loading…' : `${total} flagged row${total === 1 ? '' : 's'}`}
      </div>

      <table>
        <thead>
          <tr>
            <th>Brand</th>
            <th>Model</th>
            <th>Source notes</th>
            <th>Ø sup</th>
            <th>Len sup</th>
            <th>SKU</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id}>
              <td>{r.brand}</td>
              <td>{r.model}</td>
              <td style={{ color: 'var(--warning)', fontSize: 12 }}>{r.source_notes}</td>
              <td>{r.suppressor_diameter || '—'}</td>
              <td>{r.suppressor_length || '—'}</td>
              <td className="sku">{r.base_sku}</td>
              <td><button onClick={() => setEditing(r)}>Resolve</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      {total > 0 && (
        <div className="pagination">
          <span>{loading ? 'Loading…' : `Showing ${offset + 1}-${Math.min(offset + items.length, total)} of ${total}`}</span>
          <div className="button-row">
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Prev</button>
            <button disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
          </div>
        </div>
      )}

      {editing && (
        <SuppressorEditor
          initial={{ ...editing, needs_review: 0 }}
          onCancel={() => setEditing(null)}
          onSave={async (changes) => {
            await api.suppressors.update(editing.id, changes);
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </>
  );
}
